import React, { useState, useContext, useEffect, useRef } from "react";
import { Button, Modal, Table, Form, Spinner, OverlayTrigger, Tooltip, Dropdown, Badge, Row, Col } from "react-bootstrap";

import NotyfContext from "../contexts/NotyfContext";

import { UserSettingsContext } from "../contexts/UserSettingsContext";

import { ExternalLink, Copy, Trash2, MoreHorizontal, Check } from "react-feather";
import getActivityDateTimeDisplay from "./datetime";
import authenticatedFetch from "../utils/fetch"

import TaskAssigneeSelect from "./TaskAssigneeSelect";
import TaskAdditionalInstructionsModal from "./TaskAdditionalInstructionsModal";
import rawTaskCategories from "./taskCategories";
import rawTaskStatuses from "./taskStatuses";
import { colorsFor } from "./TaskStatusBadge";
import rawPsvTypes from "./psvTypes";
import UsStateSelect from "./UsStateSelect";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

import NextActionDatePicker from "./NextActionDatePicker";
import rawPsvResults from "./psvResults";


// allowedPracticeIds optionally restricts the options to those practices
const PracticeSelect = ({taskId, selectedPracticeId, onSelectedName, disabled, handlePracticeChanged, allowedPracticeIds, isInvalid}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Let the parent show the selected item's name (e.g. in the modal title)
    useEffect(() => {
      if (!onSelectedName) return;
      const practice = (data || []).find((practice) => practice.id === Number(selectedPracticeId));
      onSelectedName(practice ? (practice.name) : "");
    }, [data, selectedPracticeId]);
  
    useEffect(() => {
      authenticatedFetch({
        path: "practice/revenueteam",
        additionalHeaders: {"taskId": taskId},
        successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function(error) {
          setIsLoaded(true);
          setError(error);
        }
      });
    }, [taskId])
  
    if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let practiceOptionList = [<option value={0} key={0}>Select a practice...</option>];
      const visible = allowedPracticeIds ? data.filter((practice) => allowedPracticeIds.map(Number).includes(practice.id)) : data;
      practiceOptionList.push(visible.map((practice, i) => {
        return (
          <option value={practice.id} key={practice.id}>{practice.name}</option>
        )
      }, this));
  
      return (
        <Form.Select disabled={disabled} isInvalid={isInvalid} onChange={(e) => {handlePracticeChanged(e.target.value, e)}} value={selectedPracticeId}>{practiceOptionList}</Form.Select>
      )
    }
  }
  
  const CarrierSelect = ({ taskId, practiceId, disabled, selectedCarrierId, onSelectedName, handleCarrierChanged}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Let the parent show the selected item's name (e.g. in the modal title)
    useEffect(() => {
      if (!onSelectedName) return;
      const carrier = (data || []).find((carrier) => carrier.id === Number(selectedCarrierId));
      onSelectedName(carrier ? (carrier.name) : "");
    }, [data, selectedCarrierId]);
  
    useEffect(() => {
      if (practiceId > 0)
      {
        authenticatedFetch({
          path: "carrier/practice/" + practiceId,
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
      }
      else
      {
        setData([]);
      }
    }, [practiceId, taskId])
  
    if (practiceId == null || practiceId <= 0)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Please first select a practice...</option></Form.Select>);
    }
    else if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let carrierOptionList = [<option value={0} key={0}>Select a carrier...</option>];
      carrierOptionList.push(data.map((carrier, i) => {
        return (
          <option value={carrier.id} key={carrier.id}>{carrier.name}</option>
        )
      }, this));
  
      return (
        <Form.Select disabled={disabled} onChange={(e) => {handleCarrierChanged(e.target.value, e)}} value={selectedCarrierId}>{carrierOptionList}</Form.Select>
      )
    }
  }
  
  // allowedProviderId optionally restricts the options to that one provider
  const ProviderSelect = ({ taskId, practiceId, disabled, selectedProviderId, onSelectedName, handleProviderChanged, allowedProviderId}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Let the parent show the selected item's name (e.g. in the modal title)
    useEffect(() => {
      if (!onSelectedName) return;
      const provider = (data || []).find((provider) => provider.id === Number(selectedProviderId));
      // Same format as the task card: first middle last, credentials
      onSelectedName(provider ? [provider.firstName, provider.middleName, provider.lastName].filter(Boolean).join(" ") + (provider.credentials ? ", " + provider.credentials : "") : "");
    }, [data, selectedProviderId]);

    useEffect(() => {
      if (practiceId > 0)
      {
        authenticatedFetch({
          path: "provider/practice/" + practiceId,
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
      }
      else
      {
        setData([]);
      }
    }, [practiceId, taskId])

    // Preselect the allowed provider once the practice's providers load, if it is among them
    useEffect(() => {
      if (allowedProviderId && !(selectedProviderId > 0) && data.some((provider) => provider.id === Number(allowedProviderId)))
      {
        handleProviderChanged(allowedProviderId, null);
      }
    }, [data])
  
    if (practiceId == null || practiceId <= 0)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Please first select a practice...</option></Form.Select>);
    }
    else if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let providerOptionList = [<option value={0} key={0}>Select a provider...</option>];
      const visible = allowedProviderId ? data.filter((provider) => provider.id === Number(allowedProviderId)) : data;
      providerOptionList.push(visible.map((provider, i) => {
        return (
          <option value={provider.id} key={provider.id}>{provider.lastName}, {provider.firstName} {provider.middleName}</option>
        )
      }, this));
  
      return (
        <Form.Select disabled={disabled} onChange={(e) => {handleProviderChanged(e.target.value, e)}} value={selectedProviderId}>{providerOptionList}</Form.Select>
      )
    }
  }
  
const HubSelect = ({ taskId, providerId, practiceId, disabled, selectedHubId, onSelectedName, handleHubChanged}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Let the parent show the selected item's name (e.g. in the modal title)
    useEffect(() => {
      if (!onSelectedName) return;
      const hub = (data || []).find((hub) => hub.id === Number(selectedHubId));
      onSelectedName(hub ? (hub.name) : "");
    }, [data, selectedHubId]);
  
    useEffect(() => {
      if (providerId > 0)
      {
        authenticatedFetch({
          path: "hub/provider/" + providerId,
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
      }
      else if (practiceId > 0)
      {
        authenticatedFetch({
          path: "hub/practice/" + practiceId,
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
      }
      else
      {
        setData([]);
      }
    }, [providerId, practiceId, taskId])
  
    if ((providerId == null || providerId <= 0) && (practiceId == null || practiceId <= 0))
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Please first select a provider or practice...</option></Form.Select>);
    }
    else if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let hubOptionList = [<option value={0} key={0}>Select a data hub ({providerId != null && providerId > 0 ? "provider" : "practice"})...</option>];
      hubOptionList.push(data.map((hub, i) => {
        return (
          <option value={hub.id} key={hub.id}>{hub.name}</option>
        )
      }, this));
  
      return (
        <Form.Select disabled={disabled} onChange={(e) => {handleHubChanged(e.target.value, e)}} value={selectedHubId}>{hubOptionList}</Form.Select>
      )
    }
}

const FacilitySelect = ({ taskId, practiceId, disabled, selectedFacilityId, onSelectedName, handleFacilityChanged}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Let the parent show the selected item's name (e.g. in the modal title)
    useEffect(() => {
      if (!onSelectedName) return;
      const facility = (data || []).find((facility) => facility.id === Number(selectedFacilityId));
      onSelectedName(facility ? (facility.name) : "");
    }, [data, selectedFacilityId]);
  
    useEffect(() => {
      if (practiceId > 0)
      {
        authenticatedFetch({
          path: "facility/practice/" + practiceId,
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
      }
      else
      {
        setData([]);
      }
    }, [practiceId, taskId])
  
    if (practiceId == null || practiceId <= 0)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Please first select a practice...</option></Form.Select>);
    }
    else if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let facilityOptionList = [<option value={0} key={0}>Select a location...</option>];
      facilityOptionList.push(data.map((facility, i) => {
        return (
          <option value={facility.id} key={facility.id}>{facility.name}</option>
        )
      }, this));
  
      return (
        <Form.Select disabled={disabled} onChange={(e) => {handleFacilityChanged(e.target.value, e)}} value={selectedFacilityId}>{facilityOptionList}</Form.Select>
      )
    }
}

const NewAttestationDateModal = ({ show, onHide, onComplete, newAttestationDate, handleNewAttestationDateChanged, handleSkip}) => {
  const handleComplete = () => {
    if (newAttestationDate === null || newAttestationDate === "")
    {
      alert("Please specify next attestation date");
      return;
    }

    onComplete();
  }

  const setToDaysFromToday = (days) => {
    const newDate = new Date();

    const target = new Date(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate() + days
    );
    
    const year = target.getFullYear();
    const month = String(target.getMonth() + 1).padStart(2, "0");
    const day = String(target.getDate()).padStart(2, "0");

    // Local date string, no UTC conversion
    handleNewAttestationDateChanged(`${year}-${month}-${day}`);
  }

  const currentDaysFromToday = () => {
    if (newAttestationDate === null || newAttestationDate === "")
    {
      return null;
    }

    const today = new Date();

    let parts = newAttestationDate.split("-");
    const attestationDate = new Date(parts[0], parts[1] - 1, parts[2]);  // Avoid the UTC->local timezone translation issue with js

    const diffTime = attestationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  useEffect(() => {
    if (show && !newAttestationDate)
    {
      setToDaysFromToday(120);
    }
  }, [show])

  return (
      <Modal show={show} onHide={onHide} centered dialogClassName="modal-popover-card modal-compact-prompt">
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title as="div" style={{fontSize: "0.95rem", fontWeight: 500, color: "#2F7469"}}>Specify Next Attestation Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>Next Attestation Date:</td>
                  <td style={{whiteSpace: 'nowrap'}}>
                    <Button size="sm" disabled={currentDaysFromToday() === 90} onClick={() => {setToDaysFromToday(90)}}>90 Days</Button>&nbsp;
                    <Button size="sm" disabled={currentDaysFromToday() === 120} onClick={() => {setToDaysFromToday(120)}}>120 Days</Button>&nbsp;
                    <Button size="sm" disabled={currentDaysFromToday() === 180} onClick={() => {setToDaysFromToday(180)}}>180 Days</Button>
                  </td>
                  <td><Form.Control onChange={(e) => {handleNewAttestationDateChanged(e.target.value)}} type="date" value={newAttestationDate} placeholder="New attestation date" /></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
            <Button className="me-auto btn-secondary" onClick={handleSkip} size="sm">Skip</Button>
            <Button onClick={handleComplete}>Complete Task</Button>
        </Modal.Footer>
      </Modal>
  );
}

const NewExpirationDateDateModal = ({ show, onHide, onComplete, forceNewExpirationRefresh, newExpirationDate, handleNewExpirationDateChanged, noExpirationDate, handleNoExpirationDateChanged, isInitial, newEffectiveDate, handleNewEffectiveDateChanged, newPayorNumber, handleNewPayorNumberChanged, showSendEmailNotification, sendEmailNotification, handleSendEmailNotificationChanged, handleSkip, handleNewParticipationFilesChanged, isCarrier}) => {
  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const canEditDocuments = isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT");
  
  const onEffectiveDateChanged = (value, e) => {
    handleNewEffectiveDateChanged(value, e);

    if ((!newExpirationDate || newExpirationDate === "") && !noExpirationDate)
    {
      var parts = value.split("-");
  
      // If they just started keying in the date, it will come over as, e.g. 0002, and we don't want to interpret that as a real date and then set it as the value; just set this raw string value, e.g. 0002-12-21
      if (parseInt(parts[0]) > 1970)
      {
        var aYearFromNow = new Date(value);
        aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 3);
        aYearFromNow.setDate(aYearFromNow.getDate() - 1);
        handleNewExpirationDateChanged(aYearFromNow.toISOString().split('T')[0], e);
      }
    }
  }

  const handleComplete = () => {
    if (isInitial && (newEffectiveDate === null || newEffectiveDate === ""))
    {
      alert("Please specify effective date");
      return;
    }

    if (!noExpirationDate && (newExpirationDate === null || newExpirationDate === ""))
    {
      alert("Please specify expiration date");
      return; 
    }


    if (isInitial && isCarrier)
    {
      alert("Don't forget to activate LOBs for this provider + carrier, if applicable!")
    }

    onComplete();
  }

  return (
      <Modal show={show} onHide={onHide} centered dialogClassName="modal-popover-card modal-compact-prompt">
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title as="div" style={{fontSize: "0.95rem", fontWeight: 500, color: "#2F7469"}}>{isInitial ? "Specify Effective & Expiration Dates" : "Specify Next Expiration Date"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              {isInitial &&
              <tr>
                  <td>Effective Date:</td>
                  <td><Form.Control onChange={(e) => {onEffectiveDateChanged(e.target.value, e)}} type="date" value={newEffectiveDate} placeholder="Next effective date" /></td>
              </tr>
              }
              <tr>
                  <td>{isInitial ? "" : "Next"} <span>Expiration Date:</span>{isInitial ? <><br /><span style={{fontSize: "0.9em"}}>(No End Date&nbsp;<Form.Check checked={noExpirationDate} onChange={(e) => {handleNoExpirationDateChanged(e.target.checked)}} style={{display: "inline"}}/>)</span></> : <></>}</td>
                  <td><Form.Control key={forceNewExpirationRefresh} disabled={noExpirationDate} onChange={(e) => {handleNewExpirationDateChanged(e.target.value, e)}} type="date" value={newExpirationDate} placeholder="Next expiration date" /></td>
              </tr>
              {(isInitial && isCarrier) ?
              <tr>
                  <td>Payor Number:</td>
                  <td><Form.Control onChange={(e) => {handleNewPayorNumberChanged(e.target.value, e)}} type="text" value={newPayorNumber}/></td>
              </tr>
              : <></>
              }
              {canEditDocuments &&
              <tr>
                  <td>Enrollment Document(s):</td>
                  <td><Form.Control type="file" multiple onChange={(e) => {handleNewParticipationFilesChanged(e.target.files)}} /></td>
              </tr>
              }
              {showSendEmailNotification &&
              <tr>
                  <td>Send Email Notification:</td>
                  <td><Form.Check checked={sendEmailNotification} onChange={(e) => {handleSendEmailNotificationChanged(e.target.checked)}} /></td>
              </tr>
              }
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
            <Button className="me-auto btn-secondary" onClick={handleSkip} size="sm">Skip</Button>
            <Button onClick={handleComplete}>Complete Task</Button>
        </Modal.Footer>
      </Modal>
  );
}

const PsvResultModal = ({ show, onHide, onComplete, newPsvResult, handleNewPsvResultChanged, handleNewPsvFilesChanged, handleSkip}) => {

  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const canEditDocuments = isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT");

  if (newPsvResult == null)
  {
    newPsvResult = "";
  }

  const handleComplete = () => {
    if (newPsvResult === null || newPsvResult === "")
    {
      alert("Please specify PSV result");
      return;
    }

    onComplete();
  }

  const newPsvResultList = rawPsvResults.map((newPsvResult, i) => {
    return (
      <option key={i} value={newPsvResult.value}>{newPsvResult.label}</option>
    )
  }, this);

  return (
      <Modal show={show} onHide={onHide} centered dialogClassName="modal-popover-card modal-compact-prompt">
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title as="div" style={{fontSize: "0.95rem", fontWeight: 500, color: "#2F7469"}}>Specify Primary Source Verification Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>PSV Result:</td>
                  <td>
                    <Form.Select value={newPsvResult} onChange={(e) => {handleNewPsvResultChanged(e.target.value)}} style={{width: "auto"}} >
                        {newPsvResultList}
                    </Form.Select>
                  </td>
              </tr>
              {canEditDocuments &&
              <tr>
                  <td>Document(s):</td>
                  <td><Form.Control type="file" multiple onChange={(e) => {handleNewPsvFilesChanged(e.target.files)}} /></td>
              </tr>
              }
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
            <Button className="me-auto btn-secondary" onClick={handleSkip} size="sm">Skip</Button>
            <Button onClick={handleComplete}>Complete Task</Button>
        </Modal.Footer>
      </Modal>
  );
}

const DocumentModal = ({ show, onHide, onComplete, newDocumentFiles, handleNewDocumentFilesChanged, newDocumentExpirationDate, handleNewDocumentExpirationDateChanged, newDocumentEffectiveDate, handleNewDocumentEffectiveDateChanged, onSkip}) => {

  const handleComplete = () => {
    onComplete();
  }

  const handleSkip = () => {
    if (!window.confirm("Provider/Admin hasn't uploaded the required document(s) yet. Are you sure you want to skip this step and complete the task? We'll still create downstream tasks and a new shell data record."))
    {
      return;
    }

    onSkip();
  }

  return (
      <Modal show={show} onHide={onHide} centered dialogClassName="modal-popover-card modal-compact-prompt">
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <div>
            <Modal.Title as="div" style={{fontSize: "0.95rem", fontWeight: 500, color: "#2F7469"}}>Upload Document(s)</Modal.Title>
            <p className="mb-0 mt-1" style={{fontSize: "0.85em"}}>Please upload required document(s), and specify document details to complete this task.</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>Effective Date:</td>
                  <td><Form.Control onChange={(e) => {handleNewDocumentEffectiveDateChanged(e.target.value, e)}} type="date" value={newDocumentEffectiveDate} placeholder="Next effective date" /></td>
              </tr>
              <tr>
                  <td>Expiration Date:</td>
                  <td><Form.Control onChange={(e) => {handleNewDocumentExpirationDateChanged(e.target.value, e)}} type="date" value={newDocumentExpirationDate} placeholder="Next expiration date" /></td>
              </tr>
              <tr>
                  <td>Document(s):</td>
                  <td><Form.Control type="file" multiple onChange={(e) => {handleNewDocumentFilesChanged(e.target.files)}} /></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
            <Button className="me-auto btn-secondary" onClick={handleSkip} size="sm">Skip</Button>
            <Button disabled={newDocumentFiles == null || newDocumentFiles.length === 0 || newDocumentEffectiveDate == null || newDocumentEffectiveDate.length === 0 || newDocumentExpirationDate == null || newDocumentExpirationDate.length === 0} onClick={handleComplete}>Complete Task</Button>
        </Modal.Footer>
      </Modal>
  );
}


const BoldSignESignatureModal = ({ taskId, show, onHide, onComplete}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState({});
  const [templateName, setTemplateName] = useState("Loading...");
  
  const BOLDSIGN_ORIGIN = "https://app.boldsign.com";

  const handleHide = () => {
    if (window.confirm("Are you sure you want to hide the e-signature modal? Unsaved progress will be lost.")) {
      onHide();
    }
  }

  useEffect(() => {
    if (!show) return;

    setIsLoaded(false);
    setError(null);
    setData({});
    setTemplateName("Loading...");

    authenticatedFetch({
      path: "task/" + taskId + "/esignatureforms",
      additionalHeaders: {"taskId": taskId},
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
        if (result && result.url != null && result.templateName && result.urlIndex >= 0) 
        {
          setTemplateName(result.templateName);
        }
        else if (result.url === null && result.hasMore)
        {
          getNextDocument(result.urlIndex);  // Skip if already signed
        }
        else
        {
          completeSigning();
        }
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [taskId, show])

  useEffect(() => {
    if (!show) return;
    if (!isLoaded || error) return;
    if (!data || data.url == null || data.urlIndex < 0) return;

    function handleMessage(event) {
      if (event.origin !== BOLDSIGN_ORIGIN) return;

      switch (event.data.action) 
      {
        case "onDocumentSigned":
          if (data.hasMore) {
            getNextDocument(data.urlIndex);
          } else {
            completeSigning();
          }
          break;
        case "onDocumentSigningFailed":
          alert("Signing failed, please try again.");
          break;
        default:
          break;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);  // cleanup on next render
  }, [show, data, isLoaded, error]);

  async function completeSigning() {
    onComplete();
  }

  async function getNextDocument(currentUrlIndex) {
    setIsLoaded(false);
    setError(null);
    setData({});
    setTemplateName("Loading...");

    authenticatedFetch({
      path: "task/" + taskId + "/esignatureforms",
      additionalHeaders: {"taskId": taskId, 'requestedUrlIndex': currentUrlIndex + 1},
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
        if (result && result.url != null && result.urlIndex > 0 && result.templateName) 
        {
          setTemplateName(result.templateName);
        }
        else if (result.url === null && result.hasMore)
        {
          getNextDocument(result.urlIndex);  // Skip if already signed
        }
        else
        {
          completeSigning();
        }
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }

  let body = <></>;
  if (!isLoaded)
  {
    body = 
      <>
        <p className="lead">Loading...</p>
        <br />
        <Spinner animation="border" size="sm" />
        <br /><br />
      </>
  }
  else if (error || !data)
  {
    body = 
      <>
        <p className="lead">Error... Please reload the page...</p>
        <br /><br />
      </>
  }

  let title = "Review E-Signature Forms | " + templateName; 

  return (
      <Modal show={show} onHide={handleHide} centered dialogClassName={"modal-1200"}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1', paddingTop: "0.5rem", paddingBottom: "0.5rem"}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>{title}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{height: "700px"}}>
          {body}
          {data.url &&
            <iframe src={data.url} key={data.urlIndex} title="Document Signing" style={{height: "100%", width: "100%", border: "none"}} />
          }
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1', paddingTop: "0.5rem", paddingBottom: "0.5rem"}}>
            <Button onClick={handleHide}>Close</Button>
        </Modal.Footer>
      </Modal>
  );
}

const ESignaturePacketDownload = ({taskId}) => {
  const notyf = useContext(NotyfContext);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClicked = (e) => {
    e.preventDefault();
    setIsDownloading(true);

    authenticatedFetch({
      method: "POST",
      path:  "task/" + taskId + "/esignatureforms/files/signeddownloadurl",
      additionalHeaders: {"taskId": taskId},
      successHandler: function(result) {
        setIsDownloading(false);

        for (var i=0; i<result.urls.length; i++)
        {
          window.open(result.urls[i]);
        }
      },
      errorHandler: function(error) {
        setIsDownloading(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  return (
    isDownloading ?
    <Spinner animation="border" size="sm" />
    :
    <Button size="sm" onClick={(e) => {handleDownloadClicked(e)}}>Download packet</Button>
  )
}

const TASK_MODAL_CSS = `
.task-modal-compact .form-control, .task-modal-compact .form-select { padding: 0.2rem 0.5rem; font-size: 0.875rem; min-height: 31px; }
.task-modal-compact .form-select { padding-right: 1.75rem; }
.task-modal-compact textarea.form-control { min-height: 0; }
.task-modal-compact [class$="-control"] { min-height: 31px; font-size: 0.875rem; }
.task-modal-compact [class$="-control"] > div:first-child { padding-top: 0; padding-bottom: 0; }
.task-modal-compact [class$="-indicatorContainer"] { padding-top: 3px; padding-bottom: 3px; }
.task-modal-compact .modal-title { line-height: 1.2; }
`;

const SAVE_SHORTCUT_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘↵" : "Ctrl ↵";

// Same colors as the status badges on the task cards / open task lists
const StatusPill = ({ value, disabled, onChange }) => {
  const current = rawTaskStatuses.find((s) => s.value === value);
  const colors = colorsFor(value);
  const dot = <span style={{display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "currentColor", marginRight: 7}} />;

  return (
    <Dropdown onSelect={(key) => onChange(key)}>
      <Dropdown.Toggle disabled={disabled} variant="light" size="sm" className="rounded-pill border-0" style={{fontWeight: 500, ...colors, opacity: 1}}>
        {dot}{current ? current.label : ""}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {rawTaskStatuses.filter((s) => s.value !== "").map((s) => (
          <Dropdown.Item key={s.value} eventKey={s.value} active={s.value === value} className="py-1" style={s.value === value ? {backgroundColor: "#f1f3f4", color: "inherit"} : undefined}>
            <span className="rounded-pill d-inline-block px-2" style={{...colorsFor(s.value), fontWeight: 500, fontSize: "0.85rem"}}>{s.label}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

// Small muted label above a control
const Field = ({ label, children, className = "", error }) => (
  <div className={className} style={{marginBottom: "0.65rem"}}>
    <div className="text-muted" style={{fontSize: "0.74rem", fontWeight: 500, marginBottom: 3}}>{label}</div>
    {children}
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

// Badge colors for the status cards, taken from the same palette as the task status badges:
// warning/info = waiting on us (amber), primary = in progress with someone else (teal), success = done (solid teal)
const STATUS_BADGE_COLORS = {
  warning: colorsFor("BLOCKED_INTERNALLY"),
  info: colorsFor("BLOCKED_INTERNALLY"),
  primary: colorsFor("IN_PROGRESS"),
  success: { background: "#2F7469", color: "#fff" },
  secondary: colorsFor(),
};
const StatusBadge = ({ variant, children }) => (
  <Badge bg="" pill style={{ ...(STATUS_BADGE_COLORS[variant] || STATUS_BADGE_COLORS.secondary), fontWeight: 500 }}>{children}</Badge>
);

// Card for document upload / e-signature packet state
const StatusCard = ({ title, badge, badgeVariant = "secondary", children }) => (
  <div className="border rounded py-1 px-3 d-flex align-items-center flex-wrap" style={{gap: "0.5rem 1rem", backgroundColor: "rgba(58,175,169,0.06)"}}>
    <div style={{fontWeight: 500}}>{title}</div>
    {badge && <StatusBadge variant={badgeVariant}>{badge}</StatusBadge>}
    <div className="ms-auto d-flex align-items-center" style={{gap: "0.5rem"}}>{children}</div>
  </div>
);

const DocumentStatusCard = ({ info }) => {
  const notyf = useContext(NotyfContext);

  let badge = "Unknown";
  let variant = "secondary";
  if (info.status === "ACTIVE") { badge = "Awaiting upload"; variant = "warning"; }
  else if (info.status === "UPLOAD_LINK_NOT_SENT") { badge = "Link not sent"; variant = "secondary"; }
  else if (info.status === "COMPLETE") { badge = "Upload complete"; variant = "success"; }

  const copyLink = () => {
    navigator.clipboard.writeText(info.activeUrl).then(
      () => notyf.open({type: "success", message: "Link copied", duration: 3000, dismissable: true, ripple: true, background: '#3AAFA9'}),
      () => notyf.open({type: "error", message: "Couldn't copy link", duration: 3000, dismissable: true, ripple: true})
    );
  }

  return (
    <StatusCard title="Document upload" badge={badge} badgeVariant={variant}>
      {info.status === "ACTIVE" && info.activeUrl &&
        <>
          <Button size="sm" variant="outline-primary" onClick={copyLink}><Copy style={{height: 14, width: 14}} /> Copy link</Button>
          <Button size="sm" variant="outline-primary" href={info.activeUrl} target="_blank" rel="noreferrer"><ExternalLink style={{height: 14, width: 14}} /> Open</Button>
        </>
      }
    </StatusCard>
  );
}

const ESIGN_STEPS = ["Internal review", "Provider", "Approval"];

// status -> [steps done, label, badge variant, in progress on someone's side (vs. waiting on us)]
const ESIGN_STAGES = {
  ACTIVE_PRE_APPROVER_FORMS: [0, "Pending internal review", "warning"],
  PRE_APPROVER_FORMS_COMPLETE: [1, "Ready to send to provider", "info"],
  ACTIVE_PROVIDER_FORMS: [1, "With provider", "primary"],
  PROVIDER_FORMS_COMPLETE: [2, "Ready to send to approver", "info"],
  ACTIVE_APPROVER1_FORMS: [2, "With approver", "primary"],
  APPROVER1_FORMS_COMPLETE: [2, "Ready to send to final approver", "info"],
  ACTIVE_APPROVER2_FORMS: [2, "With final approver", "primary"],
  COMPLETE: [3, "Complete", "success"]
};

const ESignatureCard = ({ taskId, info, updating, canEdit, onReview, sendProvider, onSendProvider, sendApprover1, onSendApprover1, sendApprover2, onSendApprover2 }) => {
  const stage = ESIGN_STAGES[info.status] || [0, "Unknown", "secondary"];
  const [done, label, variant] = stage;

  const sendRow = (checked, onChange, who, name, email) => (
    <div className="d-flex align-items-center mt-2" style={{fontSize: "0.85rem", gap: 8}}>
      <Form.Check disabled={!canEdit} type="switch" id={"esign-send-" + who} checked={checked} onChange={onChange} label={"Send link to " + who} />
      <span className="text-muted" style={{fontFamily: "monospace", fontSize: "0.8rem"}}>{name} &lt;{email}&gt;</span>
    </div>
  );

  const link = (url, text) => <Button size="sm" variant="outline-primary" href={url} target="_blank" rel="noreferrer"><ExternalLink style={{height: 14, width: 14}} /> {text}</Button>;

  let action = null;
  let extra = null;
  switch (info.status) {
    case "ACTIVE_PRE_APPROVER_FORMS":
      action = <Button size="sm" onClick={onReview}>Review forms</Button>;
      break;
    case "PRE_APPROVER_FORMS_COMPLETE":
      extra = sendRow(sendProvider, onSendProvider, "provider", info.providerName, info.providerEmail);
      break;
    case "ACTIVE_PROVIDER_FORMS":
      action = link(info.activeProviderUrl, "Open forms");
      break;
    case "PROVIDER_FORMS_COMPLETE":
      extra = sendRow(sendApprover1, onSendApprover1, "approver", info.approver1Name, info.approver1Email);
      break;
    case "ACTIVE_APPROVER1_FORMS":
      action = link(info.activeApprover1Url, "Open forms");
      break;
    case "APPROVER1_FORMS_COMPLETE":
      extra = sendRow(sendApprover2, onSendApprover2, "final approver", info.approver2Name, info.approver2Email);
      break;
    case "ACTIVE_APPROVER2_FORMS":
      action = link(info.activeApprover2Url, "Open forms");
      break;
    case "COMPLETE":
      action = <ESignaturePacketDownload taskId={taskId} />;
      break;
    default:
      break;
  }

  const missing = info.status === "COMPLETE" && info.missingPacketDependencies && info.missingPacketDependencies.length > 0 ? info.missingPacketDependencies.join("; ") : null;

  return (
    <div className="border rounded px-3 py-1" style={{backgroundColor: "rgba(58,175,169,0.06)"}}>
      <div className="d-flex align-items-center" style={{gap: 10}}>
        <span style={{fontWeight: 600, fontSize: "0.9rem"}}>E-signature packet</span>
        {updating ? <><Spinner animation="border" size="sm" /><span className="text-muted" style={{fontSize: "0.85rem"}}>Updating...</span></> : <StatusBadge variant={variant}>{label}</StatusBadge>}
        {missing &&
          <OverlayTrigger placement="bottom" overlay={<Tooltip>{missing}</Tooltip>}>
            <span className="text-warning" style={{cursor: "help"}}><FontAwesomeIcon icon={faInfoCircle} /> Missing dependencies</span>
          </OverlayTrigger>
        }
        <span className="d-inline-flex align-items-center" style={{gap: 4}} title={ESIGN_STEPS.map((step, i) => step + (i < done ? " ✓" : i === done ? " (current)" : "")).join(" › ")}>
          {ESIGN_STEPS.map((step, i) => (
            <React.Fragment key={step}>
              {i > 0 && <span style={{width: 14, height: 2, backgroundColor: i <= done ? "#2F7469" : "#dfe5e7"}} />}
              <span style={{width: 10, height: 10, borderRadius: "50%", boxSizing: "border-box", backgroundColor: i < done ? "#2F7469" : "#fff", border: "2px solid " + (i <= done ? "#2F7469" : "#ced4da")}} />
            </React.Fragment>
          ))}
        </span>
        <div className="ms-auto">{!updating && action}</div>
      </div>
      {extra}
    </div>
  );
}

const noteInitials = (name) => {
  const parts = (name || "?").replace(/\(.*?\)/g, "").trim().split(/\s+/);
  return ((parts[0] || "?")[0] + (parts.length > 1 ? parts[1][0] : "")).toUpperCase();
}

// One entry in the notes timeline. Other event types (field changes etc.) can be rendered the same way
const TimelineNote = ({ id, user, notes, created, canDelete, onDelete, isLast }) => (
  <div className="d-flex" style={{gap: 8}}>
    <div className="d-flex flex-column align-items-center" style={{width: 24}}>
      <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{width: 24, height: 24, borderRadius: "50%", backgroundColor: "#DEF2F1", color: "#2B7A78", fontSize: "0.6rem", fontWeight: 600}}>
        {noteInitials(user)}
      </div>
      {!isLast && <div className="flex-grow-1" style={{width: 2, backgroundColor: "#e3e8ea", marginTop: 4}} />}
    </div>
    <div className="flex-grow-1 pb-3" style={{minWidth: 0}}>
      <div className="d-flex align-items-baseline">
        <span style={{fontWeight: 600, fontSize: "0.82rem"}}>{user}</span>
        <small className="text-muted ms-2">{getActivityDateTimeDisplay(created)}</small>
        {canDelete && <Trash2 style={{height: 13, width: 13}} className="ms-auto text-muted" cursor="pointer" onClick={(e) => onDelete(e, id)} />}
      </div>
      <div style={{whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.85rem", lineHeight: 1.35}}>{notes}</div>
    </div>
  </div>
);

const TaskModal = ({ id, category, practiceId, psvType, state, carrierId, providerId, hubId, facilityId, other, referenceNumber, assignees, status, due, deadline, archived, activityList, eSignatureTokenInfo, documentTokenInfo, tracked, show, onCancel, onSave, onRefresh, onDelete, allowedPracticeIds, allowedProviderId, additionalInstructionsEntityName, practiceName }) => {
    const [localId] = useState(id);
    const [localStatus, setLocalStatus] = useState(status);
    const [localCategory, setLocalCategory] = useState(category);
    const [localPsvType, setLocalPsvType] = useState(psvType);
    const [localState, setLocalState] = useState(state);
    const [localPracticeId, setLocalPracticeId] = useState(practiceId);
    const [localCarrierId, setLocalCarrierId] = useState(carrierId);
    const [localProviderId, setLocalProviderId] = useState(providerId);
    const [localHubId, setLocalHubId] = useState(hubId);
    const [localFacilityId, setLocalFacilityId] = useState(facilityId);
    const [localOther, setLocalOther] = useState(other);
    const [localReferenceNumber, setLocalReferenceNumber] = useState(referenceNumber);
    const [localDue, setLocalDue] = useState(due);
    const [localDeadline, setLocalDeadline] = useState(deadline);
    const [localArchived, setLocalArchived] = useState(archived);
    const [localTracked, setLocalTracked] = useState(tracked);
    const [isSaving, setIsSaving] = useState(false);
    const noteInputRef = useRef(null);
    const [names, setNames] = useState({practice: "", carrier: "", provider: "", hub: "", facility: ""});
  
    const [newActivity, setNewActivity] = useState('');
    // Set when a save is attempted without these; each clears once the field is filled in
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    const [noteMissing, setNoteMissing] = useState(false);
    const [practiceMissing, setPracticeMissing] = useState(false);
    const [dueMissing, setDueMissing] = useState(false);
    const [localActivityDeletes, setLocalActivityDeletes] = useState([]);

    const [showNewAttestationDateModal, setShowNewAttestationDateModal] = useState(false);
    const [newAttestationDate, setNewAttestationDate] = useState('');

    const [showNewExpirationDateModal, setShowNewExpirationDateModal] = useState(false);
    const [newExpirationDate, setNewExpirationDate] = useState('');
    const [noExpirationDate, setNoExpirationDate] = useState(false);
    const [forceNewExpirationRefresh, setForceNewExpirationRefresh] = useState(0);
    const [newEffectiveDate, setNewEffectiveDate] = useState('');
    const [newPayorNumber, setNewPayorNumber] = useState('');
    const [newParticipationFiles, setNewParticipationFiles] = useState([]);
    const [sendEmailNotification, setSendEmailNotification] = useState(false);

    const [showPsvResultModal, setShowPsvResultModal] = useState(false);
    const [newPsvResult, setNewPsvResult] = useState('');
    const [newPsvFiles, setNewPsvFiles] = useState([]);

    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [newDocumentFiles, setNewDocumentFiles] = useState([]);
    const [newDocumentExpirationDate, setNewDocumentExpirationDate] = useState('');
    const [newDocumentEffectiveDate, setNewDocumentEffectiveDate] = useState('');

    const [localSendProviderNotification, setLocalSendProviderNotification] = useState(false);
    const [localSendApprover1Notification, setLocalSendApprover1Notification] = useState(false);
    const [localSendApprover2Notification, setLocalSendApprover2Notification] = useState(false);
    const [showBoldSignESignatureModal, setShowBoldSignESignatureModal] = useState(false);
    const [packetStatusUpdating, setPacketStatusUpdating] = useState(false);

    const [taskSettings, setTaskSettings] = useState(null);
  
    const { isH3Admin, authorities } = useContext(UserSettingsContext);
    const canEdit = isH3Admin || authorities.includes("TASK_EDIT");
  
    const notyf = useContext(NotyfContext);

    useEffect(() => {
      if (!show || !isH3Admin) return;

      authenticatedFetch({
        path: "task/settings",
        additionalHeaders: {"taskId": id},
        successHandler: function(result) {
          setTaskSettings(result)
        }
      });
    }, [show, isH3Admin, id]);
      
    // On create, may not touch these
    if (localStatus == null) {
      setLocalStatus("CREATED")
    }
  
    if (localCategory == null) {
      setLocalCategory("INITIAL_ENROLLMENT");
    }

    if (localPsvType == null) {
      setLocalPsvType("");
    }

    if (localState == null) {
      setLocalState("");
    }
  
    if (localOther == null) {
      setLocalOther("");
    }

    if (localReferenceNumber == null) {
      setLocalReferenceNumber("");
    }

    // This will always be touched, but react doesn't like us starting it off as null
    if (localDue == null) {
      let twoBizDays = new Date();
      twoBizDays.setDate(twoBizDays.getDate() + 2);

      if (twoBizDays.getDay() === 6 || twoBizDays.getDay() === 0)
      {
          twoBizDays.setDate(twoBizDays.getDate() + 2);
      }

      setLocalDue(twoBizDays.getFullYear() + "-" + String(twoBizDays.getMonth() + 1).padStart(2, '0') + "-" + String(twoBizDays.getDate()).padStart(2, '0'));
    }

    if (localDeadline == null) {
      setLocalDeadline("");
    }
  
    // Have to format the local assignees here to structure the plugin can understand
    let formattedAssignees = null;  // Null to allow task select to auto-select first assignee
    if (assignees != null && assignees.length > 0)
    {
      formattedAssignees = [];
      for (var i=0; i<assignees.length; i++)
      {
        formattedAssignees.push({value: assignees[i].userId, label:assignees[i].firstName + ' ' + assignees[i].lastName});
      }
    }
    const [localAssignees, setLocalAssignees] = useState(formattedAssignees);
  
    const handleAssigneesChanged = (assignees) => {
      setLocalAssignees(assignees);
    }
  
    const handleStatusChanged = (status, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalStatus(status);
      if (status === "COMPLETE") setDueMissing(false);  // the date isn't required once completing
    }
  
    const handleCategoryChanged = (category, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalCategory(category);

      if (category !== "PSV")
      {
        setLocalPsvType("");
        setLocalState("");
      }
    }

    const handlePsvTypeChanged = (psvType, e) => {
      if (e != null) {
        e.preventDefault();
      }

      setLocalPsvType(psvType);
    }

    function handleStateChanged(name, newValue) {
      setLocalState(newValue);
    }
  
    const handlePracticeChanged = (practiceId, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalPracticeId(practiceId);
      setPracticeMissing(false);
  
      // Clear the other values that are dependent on practice
      setLocalCarrierId(0);
      setLocalProviderId(0);
      setLocalHubId(0);
      setLocalFacilityId(0);
    }
  
    const handleCarrierChanged = (carrierId, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalCarrierId(carrierId);
    }
  
    const handleProviderChanged = (providerId, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalProviderId(providerId);
      setLocalHubId(0);
    }
  
    const handleHubChanged = (hubId, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalHubId(hubId);
    }
  
    const handleFacilityChanged = (facilityId, e) => {
        if (e != null) {
          e.preventDefault();
        }
    
        setLocalFacilityId(facilityId);
      }

    const handleOtherChanged = (other, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalOther(other);
    }

    const handleReferenceNumberChanged = (referenceNumber, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalReferenceNumber(referenceNumber);
    }
  
    const handleDueChanged = (due) => {
      setLocalDue(due);
      setDueMissing(false);
    }

    const handleDeadlineChanged = (deadline, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setLocalDeadline(deadline);
    }

    const handleArchivedChanged = (archived) => {
      setLocalArchived(archived);
    }

    const handleTrackedChanged = (tracked) => {
      setLocalTracked(tracked);
    }
    
    const handleNewActivityChanged = (newActivity, e) => {
      if (e != null) {
        e.preventDefault();
      }
  
      setNewActivity(newActivity);
      setNoteMissing(false);
    }

    const handleActivityDelete = (e, activityId) => {
      e.preventDefault();

      if (!window.confirm("Are you sure you want to delete this note?"))
      {
        return;
      }

      setLocalActivityDeletes([...localActivityDeletes, activityId]);
    }

    const handleNoExpirationDateChanged = (val) => {
      setNoExpirationDate(val);
      
      if (val)
      {
        setNewExpirationDate('');
        // If the date entered is incomplete (e.g. 02/07/yyyy), the internal value stays blank, so setting it here doesn't trigger a re-render; use a force refresh to make sure we blank it out
        setForceNewExpirationRefresh(forceNewExpirationRefresh + 1);
      }
    }

    const handleSendProviderNotificationChanged = (e) => {
      setLocalSendProviderNotification(e.target.checked);
    }

    const handleSendApprover1NotificationChanged = (e) => {
      setLocalSendApprover1Notification(e.target.checked);
    }

    const handleSendApprover2NotificationChanged = (e) => {
      setLocalSendApprover2Notification(e.target.checked);
    }
    
  
    const reset = () => {
      setLocalStatus(status);
      setLocalCategory(category);
      setLocalPsvType(psvType);
      setLocalState(state);
      setLocalPracticeId(practiceId);
      setLocalCarrierId(carrierId);
      setLocalProviderId(providerId);
      setLocalHubId(hubId);
      setLocalFacilityId(facilityId);
      setLocalOther(other);
      setLocalReferenceNumber(referenceNumber);
      setLocalDue(due);
      setLocalDeadline(deadline);
      setLocalArchived(archived);
      setLocalTracked(tracked);
      setLocalAssignees(formattedAssignees);
      setNewActivity('');
      setNoteMissing(false);
      setPracticeMissing(false);
      setDueMissing(false);
      setLocalActivityDeletes([]);
      setIsSaving(false);
      setNewAttestationDate('');
      setNewExpirationDate('');
      setNoExpirationDate(false);
      setNewEffectiveDate('');
      setNewPsvResult('');
      setNewPsvFiles([]);
      setNewDocumentFiles([]);
      setNewDocumentExpirationDate('');
      setNewDocumentEffectiveDate('');
      setNewPayorNumber('');
      setNewParticipationFiles([]);
      setSendEmailNotification(false);
      setLocalSendProviderNotification(false);
      setLocalSendApprover1Notification(false);
      setLocalSendApprover2Notification(false);
    }
  
    const handleCancel = () => {
      reset();
      onCancel();
    }
  
    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this task?"))
        {
          authenticatedFetch({
            path: "task/" + id,
            additionalHeaders: {"taskId": id},
            method: "DELETE",
            successHandler: function(result) {
              onDelete();
  
              notyf.open({type: "error", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
            },
            errorHandler: function(error) {
              alert("Something went wrong; please try again");
            }
          });
        }
    }

    const doSubmit = (expirationOrAttestationModalSkipped=false) => {
      const requestData = {
        id: localId,
        status: localStatus,
        category: localCategory,
        psvType: localPsvType,
        state: localState,
        practiceId: localPracticeId,
        carrierId: localCarrierId,
        providerId: localProviderId,
        hubId: localHubId,
        facilityId: localFacilityId,
        other: localOther,
        referenceNumber: localReferenceNumber,
        due: localDue,
        deadline: localDeadline,
        archived: localArchived,
        tracked: localTracked,
        newActivity: newActivity,
        activityDeletes: localActivityDeletes,
        assignees: localAssignees,
        newAttestationDate: expirationOrAttestationModalSkipped ? null : newAttestationDate,
        newExpirationDate: expirationOrAttestationModalSkipped ? null : newExpirationDate,
        newEffectiveDate: expirationOrAttestationModalSkipped ? null : newEffectiveDate,
        newPayorNumber: expirationOrAttestationModalSkipped ? null : newPayorNumber,
        newPsvResult: expirationOrAttestationModalSkipped ? null : newPsvResult,
        newPsvFiles: expirationOrAttestationModalSkipped ? [] : newPsvFiles,
        newDocumentFiles: expirationOrAttestationModalSkipped ? [] : newDocumentFiles,
        newDocumentExpirationDate: expirationOrAttestationModalSkipped ? null : newDocumentExpirationDate,
        newDocumentEffectiveDate: expirationOrAttestationModalSkipped ? null : newDocumentEffectiveDate,
        newParticipationFiles: expirationOrAttestationModalSkipped ? [] : newParticipationFiles,
        sendEmailNotification: expirationOrAttestationModalSkipped ? false : sendEmailNotification,
        sendProviderNotification: localSendProviderNotification,
        sendApprover1Notification: localSendApprover1Notification,
        sendApprover2Notification: localSendApprover2Notification
      };
    
      var formData = new FormData();
      formData.append("data", new Blob([JSON.stringify(requestData)], {
          type: "application/json"
      }));

      const psvFiles = Array.from(expirationOrAttestationModalSkipped ? [] : (newPsvFiles || []));
      psvFiles.forEach((file) => {
        formData.append("newPsvFiles", file);
      });

      const documentFiles = Array.from(expirationOrAttestationModalSkipped ? [] : (newDocumentFiles || []));
      documentFiles.forEach((file) => {
        formData.append("newDocumentFiles", file);
      });

      const participationFiles = Array.from(expirationOrAttestationModalSkipped ? [] : (newParticipationFiles || []));
      participationFiles.forEach((file) => {
        formData.append("newParticipationFiles", file);
      });

      authenticatedFetch({
        path: "task",
        method: "POST",
        requestData: formData,
        formData: true,
        additionalHeaders: {"taskId": localId ? localId : null},
        successHandler: function(result) {
          setNewActivity('');
          setIsSaving(false);
  
          var message = "Updated!";
          if (localId == null || localId === 0)
          {
            message = "Created!";
            reset(); // If this is the shared "new" task modal, need to reset everything
          }

          if (result.warningMessage)
          {
            message += " " + result.warningMessage;
          }
  
          onSave();
  
          if (result.warningMessage)
          {
            notyf.open({type: "warning", message: message, duration: 5000, dismissable: true, ripple: true});
          }
          else
          {
            notyf.open({type: "success", message: message, duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
          }
        },
        errorHandler: function() {
          alert("Something went wrong; please try again");
          setNewActivity('');
          setIsSaving(false);
        }
      });
    }

    const onHideNewAttestationDateModal = () => {
      setShowNewAttestationDateModal(false);
      setIsSaving(false);
    }

    const onCompleteNewAttestationDateModal = () => {
      setShowNewAttestationDateModal(false);
      doSubmit();
    }

    const onSkipNewAttestationDateModal = () => {
      setShowNewAttestationDateModal(false);
      doSubmit(true);
    }

    const onCompleteNewExpirationDateModal = () => {
      setShowNewExpirationDateModal(false);
      doSubmit();
    }

    const onSkipNewExpirationDateModal = () => {
      setShowNewExpirationDateModal(false);
      doSubmit(true);
    }

    const onHideNewExpirationDateModal = () => {
      setShowNewExpirationDateModal(false);
      setIsSaving(false);
    }

    const onHidePsvResultModal = () => {
      setNewPsvFiles([]);
      setShowPsvResultModal(false);
      setIsSaving(false);
    }

    const onCompletePsvResultModal = () => {
      setShowPsvResultModal(false);
      doSubmit();
    }

    const onSkipPsvResultModal = () => {
      setShowPsvResultModal(false);
      doSubmit(true);
    }

    const handleNewPsvFilesChanged = (files) => {
      setNewPsvFiles(files || []);
    }

    const onHideDocumentModal = () => {
      setNewDocumentFiles([]);
      setNewDocumentExpirationDate('');
      setNewDocumentEffectiveDate('');
      setShowDocumentModal(false);
      setIsSaving(false);
    }

    const onCompleteDocumentModal = () => {
      setShowDocumentModal(false);
      doSubmit();
    }

    const onSkipDocumentModal = () => {
      setShowDocumentModal(false);
      doSubmit(true);
    }

    const handleNewDocumentFilesChanged = (files) => {
      setNewDocumentFiles(files || []);
    }

    const handleNewDocumentExpirationDateChanged = (val, e) => {
      setNewDocumentExpirationDate(val);
    }

    const handleNewDocumentEffectiveDateChanged = (val, e) => {
      setNewDocumentEffectiveDate(val);
    }

    const handleNewParticipationFilesChanged = (files) => {
      setNewParticipationFiles(files || []);
    }

    const handleReviewFormsClicked = () => {
      setShowBoldSignESignatureModal(true);
    }

    const onCompleteReviewForms = () => {
      setShowBoldSignESignatureModal(false);
      setIsSaving(true);
      setPacketStatusUpdating(true);

      authenticatedFetch({
        path: "task/" + id + "/esignatureforms",
        method: "POST",
        additionalHeaders: {"taskId": id},
        successHandler: function(result) {
          setIsSaving(false);
          setPacketStatusUpdating(false);

          onRefresh();
        },
        errorHandler: function() {
          alert("Something went wrong; please try again");
          setIsSaving(false);
          setPacketStatusUpdating(false);
        }
      });
    }
  
    const handleSave = () => {
      // Check everything up front so all the problems are highlighted at once
      const noPractice = !localPracticeId || Number(localPracticeId) <= 0;
      const noDue = localStatus !== "COMPLETE" && !localDue;           // next action date, unless completing
      const noNote = !!(localId && localId > 0) && newActivity.trim() === "";  // updates always need a note

      setPracticeMissing(noPractice);
      setDueMissing(noDue);
      setNoteMissing(noNote);

      if (noPractice || noDue || noNote)
      {
        if (!noPractice && !noDue && noteInputRef.current) noteInputRef.current.focus();
        return;
      }

      setIsSaving(true);
  
      if (localStatus === "COMPLETE" && status !== "COMPLETE")
      {
        if (localCategory === "ATTESTATION" && localHubId && localHubId !== 0 && localHubId !== '0')
        {
          setShowNewAttestationDateModal(true);
          return;
        }
        else if ((localCategory === "INITIAL_ENROLLMENT" || localCategory === "ANNUAL_ENROLLMENT") && ((localCarrierId && localCarrierId !== 0 && localCarrierId !== '0') || (localFacilityId && localFacilityId !== 0 && localFacilityId !== '0')))
        {
          if (localCategory === "INITIAL_ENROLLMENT")
          {
            // Default to true
            setSendEmailNotification(true);
          }
          else
          {
            setSendEmailNotification(false);
          
          }

          setShowNewExpirationDateModal(true);
          return;
        }
        else if (localCategory === "PSV")
        {
          setShowPsvResultModal(true);
          return;
        }
        else if (hasAssociatedDocumentUpload && documentTokenInfo && (documentTokenInfo.status === "ACTIVE" || documentTokenInfo.status === "UPLOAD_LINK_NOT_SENT"))
        {
          const canEditDocuments = isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT");

          if (canEditDocuments)
          {
            // Make sure status isn't stale
            authenticatedFetch({
              path: "task/" + id + "/documentstatus",
              additionalHeaders: {"taskId": id},
              successHandler: function(result) {
                if (result && (result.status === "ACTIVE" || result.status === "UPLOAD_LINK_NOT_SENT"))
                {
                  setShowDocumentModal(true);
                }
                else
                {
                  doSubmit();
                }
              },
              errorHandler: function() {
                alert("Something went wrong; please try again.");
                setIsSaving(false);
              }
            });

            return;
          }
        }
      }

      doSubmit();
    }
  
    // Same headline logic as the open task rows: carrier > location > data hub > PSV > category
    const categoryLabels = {INITIAL_ENROLLMENT: "Initial Enrollment", ANNUAL_ENROLLMENT: "Re-enrollment", ATTESTATION: "Attestation", INQUIRY: "Inquiry", TERMINATION: "Termination", PSV: "PSV", OTHER: "Other"};
    const selectedPsvType = rawPsvTypes.find((t) => t.value === localPsvType && t.value !== "");
    const psvDisplay = selectedPsvType ? selectedPsvType.label + (localState ? " | " + localState : "") : "";
    const modalHeadline = names.carrier || names.facility || names.hub || psvDisplay || categoryLabels[localCategory] || "";
    const modalTitle = [names.provider, modalHeadline].filter(Boolean).join(" · ");
    const modalSubtitleParts = [id > 0 ? "#" + id : "New task"];
    if (localOther) modalSubtitleParts.push(localOther);
    if (psvDisplay && modalHeadline !== psvDisplay) modalSubtitleParts.push(psvDisplay);
    if (names.practice) modalSubtitleParts.push(names.practice);

    // Notes shown newest first, composer on top
    const timelineNotes = (activityList || []).filter((activity) => !localActivityDeletes.includes(activity.id));

    let psvTypeOptionList = rawPsvTypes.map((psvType, i) => {
      // Remove the blank option
      if (psvType.value === "") {
        return null;
      }

      return (
        <option key={i} value={psvType.value}>{psvType.label}</option>
      )
    }, this);
  
    let hasAssociatedEsignature = (eSignatureTokenInfo != null);

    // TODO packet generation will have other dependencies as well, so ready for packet generation is dependent on those being met also, once the signature proces is done

    let hasAssociatedDocumentUpload = (documentTokenInfo != null);

    return (
      <>
        <Modal show={show} onHide={handleCancel} centered dialogClassName="modal-850 task-modal-compact modal-popover-card" onEntered={() => { if (noteInputRef.current) noteInputRef.current.focus(); }}>
          <style>{TASK_MODAL_CSS}</style>
          <Modal.Header closeButton style={{backgroundColor: '#DEF2F1', padding: "0.7rem 1rem"}}>
            <div className="d-flex align-items-center" style={{minWidth: 0, flex: 1, gap: "0.6rem"}}>
              <Modal.Title as="div" style={{fontSize: "0.95rem", fontWeight: 500, color: "#2F7469", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 1, minWidth: 0}}>{modalTitle}</Modal.Title>
              <div className="d-flex" style={{fontSize: "0.8rem", color: "#4a5154", whiteSpace: "nowrap", flex: "1 1 0", minWidth: 0}}>
                {modalSubtitleParts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="flex-shrink-0">&nbsp;·&nbsp;</span>}
                    {/* each part truncates on its own so some of every part stays visible */}
                    <span style={{flex: i === 0 ? "0 0 auto" : "0 1 auto", minWidth: i === 0 ? undefined : "3em", overflow: "hidden", textOverflow: "ellipsis"}} title={part}>{part}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 ms-2 me-2"><StatusPill value={localStatus} disabled={!canEdit} onChange={(v) => handleStatusChanged(v, null)} /></div>
          </Modal.Header>
          <Modal.Body style={{padding: "0.85rem 1.1rem 1rem"}} onKeyDown={(e) => { if (canEdit && (e.metaKey || e.ctrlKey) && e.key === "Enter" && !isSaving) { e.preventDefault(); handleSave(); } }}>
            <Row className="gx-3">
              <Col md={3}>
                <Field label="Next Action" error={dueMissing && "Required"}>
                  {isH3Admin ?
                  <NextActionDatePicker taskId={id} disabled={!canEdit} isInvalid={dueMissing} localDue={localDue} handleDueChanged={handleDueChanged} localAssignees={localAssignees}/>
                  :
                  <Form.Control disabled={!canEdit} isInvalid={dueMissing} onChange={(e) => {handleDueChanged(e.target.value)}} type="date" value={localDue} placeholder="Next action date" />
                  }
                </Field>
              </Col>
              <Col md={9}>
                <Field label="Summary / Headline">
                  <Form.Control disabled={!canEdit} onChange={(e) => {handleOtherChanged(e.target.value, e)}} value={localOther} />
                </Field>
              </Col>
            </Row>



            <div className="text-muted mb-1" style={{fontSize: "0.72rem", fontWeight: 500}}>New note</div>
            {canEdit &&
            <div className="mb-3">
              <Form.Control ref={noteInputRef} as="textarea" rows={2} value={newActivity} placeholder="Add a note..."
                isInvalid={noteMissing}
                onChange={(e) => {handleNewActivityChanged(e.target.value, e)}} />
              <Form.Control.Feedback type="invalid">Please add a note describing this update.</Form.Control.Feedback>
            </div>
            }

            <div className="border-top pt-2 mt-1">
            <Row className="gx-3">
              <Col md={4}>
                <Field label="Category">
                  <Form.Select disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} onChange={(e) => {handleCategoryChanged(e.target.value, e)}} value={localCategory}>
                    <option value="INITIAL_ENROLLMENT" key="INITIAL_ENROLLMENT">Initial Enrollment</option>
                    <option value="ANNUAL_ENROLLMENT" key="ANNUAL_ENROLLMENT">Re-enrollment</option>
                    <option value="ATTESTATION" key="ATTESTATION">Attestation</option>
                    <option value="INQUIRY" key="INQUIRY">Inquiry</option>
                    <option value="TERMINATION" key="TERMINATION">Termination</option>
                    <option value="PSV" key="PSV">PSV</option>
                    <option value="OTHER" key="OTHER">Other</option>
                  </Form.Select>
                </Field>
              </Col>
              { localCategory === "PSV" &&
              <>
              <Col md={4}>
                <Field label="PSV Type">
                  <Form.Select disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} onChange={(e) => {handlePsvTypeChanged(e.target.value, e)}} value={localPsvType}>
                    {psvTypeOptionList}
                  </Form.Select>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="State">
                  <UsStateSelect width="100%" placeholder="State" originalValue={localState} name="state" onValueChanged={handleStateChanged}/>
                </Field>
              </Col>
              </>
              }
              <Col md={4}>
                <Field label="Practice" error={practiceMissing && "Please select a practice"}>
                  <PracticeSelect onSelectedName={(n) => setNames((p) => p.practice === n ? p : {...p, practice: n})} taskId={id} disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} selectedPracticeId={localPracticeId} isInvalid={practiceMissing} handlePracticeChanged={handlePracticeChanged} allowedPracticeIds={allowedPracticeIds} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Provider">
                  <ProviderSelect onSelectedName={(n) => setNames((p) => p.provider === n ? p : {...p, provider: n})} taskId={id} disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} practiceId={localPracticeId} selectedProviderId={localProviderId} handleProviderChanged={handleProviderChanged} allowedProviderId={allowedProviderId} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Carrier">
                  <CarrierSelect onSelectedName={(n) => setNames((p) => p.carrier === n ? p : {...p, carrier: n})} taskId={id} disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} practiceId={localPracticeId} selectedCarrierId={localCarrierId} handleCarrierChanged={handleCarrierChanged} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Data Hub">
                  <HubSelect onSelectedName={(n) => setNames((p) => p.hub === n ? p : {...p, hub: n})} taskId={id} disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} providerId={localProviderId} practiceId={localPracticeId} selectedHubId={localHubId} handleHubChanged={handleHubChanged} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Location">
                  <FacilitySelect onSelectedName={(n) => setNames((p) => p.facility === n ? p : {...p, facility: n})} taskId={id} disabled={!canEdit || hasAssociatedEsignature || hasAssociatedDocumentUpload} practiceId={localPracticeId} selectedFacilityId={localFacilityId} handleFacilityChanged={handleFacilityChanged} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Assignees">
                  <TaskAssigneeSelect taskId={id} disabled={!canEdit} autoSelectFirstAssignee={id == null || id === 0} selectedAssignees={localAssignees} handleAssigneesChanged={handleAssigneesChanged} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Deadline">
                  <Form.Control disabled={!canEdit} onChange={(e) => {handleDeadlineChanged(e.target.value, e)}} type="date" value={localDeadline} placeholder="Deadline" />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Reference #">
                  <Form.Control disabled={!canEdit} onChange={(e) => {handleReferenceNumberChanged(e.target.value, e)}} value={localReferenceNumber} />
                </Field>
              </Col>
              </Row>
            </div>

            {((((localFacilityId != null && localFacilityId !== 0 && localFacilityId !== '0') || (localCarrierId != null && localCarrierId !== 0 && localCarrierId !== '0')) && (localCategory === "INITIAL_ENROLLMENT" || localCategory === "ANNUAL_ENROLLMENT") && hasAssociatedEsignature) || hasAssociatedDocumentUpload) &&
            <div className="d-flex flex-column border-top pt-2 mb-2" style={{gap: "0.5rem"}}>
            { (((localFacilityId != null && localFacilityId !== 0 && localFacilityId !== '0') || (localCarrierId != null && localCarrierId !== 0 && localCarrierId !== '0')) && (localCategory === "INITIAL_ENROLLMENT" || localCategory === "ANNUAL_ENROLLMENT") && hasAssociatedEsignature) &&
            <ESignatureCard taskId={id} info={eSignatureTokenInfo} updating={packetStatusUpdating} canEdit={canEdit} onReview={handleReviewFormsClicked}
              sendProvider={localSendProviderNotification} onSendProvider={handleSendProviderNotificationChanged}
              sendApprover1={localSendApprover1Notification} onSendApprover1={handleSendApprover1NotificationChanged}
              sendApprover2={localSendApprover2Notification} onSendApprover2={handleSendApprover2NotificationChanged} />
            }
            {hasAssociatedDocumentUpload && <DocumentStatusCard info={documentTokenInfo} />}
            </div>
            }

            {timelineNotes.length > 0 &&
            <div className="border-top pt-2 mt-0 mb-2 text-muted" style={{fontSize: "0.74rem", fontWeight: 500}}>History ({timelineNotes.length})</div>
            }
            {timelineNotes.length > 0 &&
            <div style={{maxHeight: "200px", overflowY: "auto", paddingRight: 4}}>
              {timelineNotes.map((activity, i) => (
                <TimelineNote key={activity.id} id={activity.id} user={activity.userFormattedName} notes={activity.notes} created={activity.created} canDelete={activity.canDelete} onDelete={handleActivityDelete} isLast={i === timelineNotes.length - 1} />
              ))}
            </div>
            }
          </Modal.Body>
          <Modal.Footer style={{backgroundColor: '#DEF2F1', padding: "0.5rem 1rem"}}>
            { isSaving ? 
              <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '40px', marginTop: '6px', marginBottom: '6px'}}/>
              :
              <>
                <div className="me-auto d-flex align-items-center" style={{gap: 8}}>
                {(id != null && id > 0 && (isH3Admin || (canEdit && status === "COMPLETE"))) || ((id == null || id <= 0) && canEdit && isH3Admin && taskSettings && taskSettings.showTracked) ?
                  <Dropdown drop="up">
                    <Dropdown.Toggle variant="light" size="sm" className="border" bsPrefix="btn" aria-label="More actions"><MoreHorizontal style={{height: 16, width: 16}} /></Dropdown.Toggle>
                    <Dropdown.Menu>
                      {canEdit && isH3Admin && taskSettings && taskSettings.showTracked &&
                        <Dropdown.Item onClick={() => handleTrackedChanged(!localTracked)}>
                          <span className="d-inline-block me-2" style={{width: 14}}>{localTracked && <Check style={{height: 14, width: 14}} />}</span>Tracked
                        </Dropdown.Item>
                      }
                      {canEdit && status === "COMPLETE" &&
                        <Dropdown.Item onClick={() => handleArchivedChanged(!localArchived)}>
                          <span className="d-inline-block me-2" style={{width: 14}}>{localArchived && <Check style={{height: 14, width: 14}} />}</span>Archived
                        </Dropdown.Item>
                      }
                      {id > 0 && isH3Admin && (status === "COMPLETE" || (taskSettings && taskSettings.showTracked)) && <Dropdown.Divider />}
                      {id > 0 && isH3Admin && <Dropdown.Item className="text-danger" onClick={handleDelete}><Trash2 style={{height: 14, width: 14}} className="me-2" />Delete task...</Dropdown.Item>}
                    </Dropdown.Menu>
                  </Dropdown>
                  : ''}
                {/* Same rule as the task card and list: only for editors, when the task has additional instructions */}
                {id > 0 && canEdit && additionalInstructionsEntityName &&
                  <OverlayTrigger placement="top" overlay={<Tooltip>View instructions</Tooltip>}>
                    <Button onClick={() => setShowInstructionsModal(true)} variant="primary" size="sm" aria-label="View instructions"><FontAwesomeIcon icon={faInfoCircle} /></Button>
                  </OverlayTrigger>
                }
                </div>
                <Button variant="outline-secondary" onClick={handleCancel}>{canEdit ? 'Cancel' : 'Close'}</Button>
                {canEdit ?
                <Button onClick={handleSave}>{id == null || id <= 0 ? "Create Task" : "Update Task"}<kbd style={{marginLeft: 10, padding: "0 6px", fontFamily: "inherit", fontSize: "0.72rem", fontWeight: 400, color: "#fff", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.45)", borderRadius: 4, verticalAlign: "middle"}}>{SAVE_SHORTCUT_LABEL}</kbd></Button>
                : '' }
              </>
            }
          </Modal.Footer>
        </Modal>
      {id > 0 && canEdit && additionalInstructionsEntityName &&
        <TaskAdditionalInstructionsModal show={showInstructionsModal} onClose={() => setShowInstructionsModal(false)} id={id} category={rawTaskCategories.find((c) => c.value === localCategory) || {label: ""}} entityName={additionalInstructionsEntityName} practiceName={practiceName} />
      }
      <NewAttestationDateModal show={showNewAttestationDateModal} onComplete={onCompleteNewAttestationDateModal} onHide={onHideNewAttestationDateModal} handleSkip={onSkipNewAttestationDateModal} newAttestationDate={newAttestationDate} handleNewAttestationDateChanged={(newAttestationDate) => setNewAttestationDate(newAttestationDate)} />
      <NewExpirationDateDateModal show={showNewExpirationDateModal} onComplete={onCompleteNewExpirationDateModal} onHide={onHideNewExpirationDateModal} handleSkip={onSkipNewExpirationDateModal} 
        newExpirationDate={newExpirationDate} handleNewExpirationDateChanged={(newExpirationDate) => setNewExpirationDate(newExpirationDate)} 
        noExpirationDate={noExpirationDate} handleNoExpirationDateChanged={handleNoExpirationDateChanged}
        forceNewExpirationRefresh={forceNewExpirationRefresh}
        isInitial={localCategory === "INITIAL_ENROLLMENT"} newEffectiveDate={newEffectiveDate} handleNewEffectiveDateChanged={(newEffectiveDate) => setNewEffectiveDate(newEffectiveDate)}
        newPayorNumber={newPayorNumber} handleNewPayorNumberChanged={(newPayorNumber) => setNewPayorNumber(newPayorNumber)} 
        showSendEmailNotification={!!((localCategory === "INITIAL_ENROLLMENT" || localCategory === "ANNUAL_ENROLLMENT") && (localProviderId && localProviderId !== 0 && localProviderId !== '0') && ((localCarrierId && localCarrierId !== 0 && localCarrierId !== '0') || (localFacilityId && localFacilityId !== 0 && localFacilityId !== '0')))} 
        sendEmailNotification={sendEmailNotification} handleSendEmailNotificationChanged={(sendEmailNotification) => setSendEmailNotification(sendEmailNotification)}
        handleNewParticipationFilesChanged={handleNewParticipationFilesChanged}
        isCarrier={(localCarrierId && localCarrierId !== 0 && localCarrierId !== '0')}/>
      <PsvResultModal newPsvResult={newPsvResult} handleNewPsvResultChanged={(newPsvResult) => setNewPsvResult(newPsvResult)} handleNewPsvFilesChanged={handleNewPsvFilesChanged} show={showPsvResultModal} onHide={onHidePsvResultModal} onComplete={onCompletePsvResultModal} handleSkip={onSkipPsvResultModal} />
      <DocumentModal newDocumentFiles={newDocumentFiles} handleNewDocumentFilesChanged={handleNewDocumentFilesChanged} newDocumentExpirationDate={newDocumentExpirationDate} handleNewDocumentExpirationDateChanged={handleNewDocumentExpirationDateChanged} newDocumentEffectiveDate={newDocumentEffectiveDate} handleNewDocumentEffectiveDateChanged={handleNewDocumentEffectiveDateChanged} taskId={id} show={showDocumentModal} onHide={onHideDocumentModal} onComplete={onCompleteDocumentModal} onSkip={onSkipDocumentModal} />
      <BoldSignESignatureModal taskId={id} show={showBoldSignESignatureModal} onComplete={onCompleteReviewForms} onHide={() => setShowBoldSignESignatureModal(false)} />
      </>
    );
}

  export default TaskModal;